import BackButton from "@/components/BackButton"
import { Button } from "@/components/chadcn/Button"
import { Label } from "@/components/chadcn/Label"
import { TextareaInput } from "@/components/InputFields"
import type { MedicationTaken } from "@/model/BloodGlucoseForm"
import { medicationQuestionTitle } from "@/model/Medication"
import cn from "@/util/cn"
import { useState } from "react"

const DESCRIPTION =
  "Logging when you take your medication lets us match your doses to your blood sugar readings - it's one of the most important things you can do to help your pregnancy."
const NOTE_LABEL = "Add a note (optional)"
const NOTE_PLACEHOLDER = "e.g. I took it about an hour late"
const NEXT_BUTTON = "Continue"

type Option = {
  label: string
  value: MedicationTaken
}

const options: Option[] = [
  { label: "Yes", value: "YES" },
  { label: "No", value: "NO" },
]

type Props = {
  medicationTaken: MedicationTaken | null
  note: string
  onSubmit: (values: { medicationTaken: MedicationTaken | null; note: string }) => void
  onBack?: () => void
}

const MedicationQuestionStep = ({ medicationTaken, note, onSubmit, onBack }: Props) => {
  const [answer, setAnswer] = useState<MedicationTaken | null>(medicationTaken)
  const [noteValue, setNoteValue] = useState(note)

  const submit = () => {
    onSubmit({ medicationTaken: answer, note: noteValue })
  }

  return (
    <div className="flex min-h-[calc(100vh-32px)] flex-col gap-4">
      {onBack && <BackButton customClickHandler={onBack} />}
      <h2 className="text-h2 text-primary-contrast">{medicationQuestionTitle}</h2>

      <div className="mt-2 flex flex-1 flex-col gap-6">
        <p className="text-base text-primary-contrast/80">{DESCRIPTION}</p>

        <div role="radiogroup" className="flex flex-col gap-4">
          {options.map(({ label, value }) => {
            const selected = answer === value
            return (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={selected}
                onClick={() => setAnswer(selected ? null : value)}
                className={cn(
                  "flex w-full items-center gap-4 rounded-xl border-4 px-6 py-5 text-h4 font-medium text-primary-contrast shadow-card transition-colors",
                  selected ? "border-primary bg-secondary" : "border-primary/40 bg-white"
                )}
              >
                <span
                  className={cn(
                    "flex size-6 shrink-0 items-center justify-center rounded-full border-2",
                    selected ? "border-primary" : "border-primary/40"
                  )}
                >
                  {selected && <span className="size-3 rounded-full bg-primary" />}
                </span>
                {label}
              </button>
            )
          })}
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="medication-note">{NOTE_LABEL}</Label>
          <TextareaInput
            id="medication-note"
            placeholder={NOTE_PLACEHOLDER}
            value={noteValue}
            onChange={(event) => setNoteValue(event.target.value)}
            className="min-h-[10rem] resize-none"
          />
        </div>
      </div>

      <Button type="button" onClick={submit} className="w-full py-2 text-base">
        {NEXT_BUTTON}
      </Button>
    </div>
  )
}

export default MedicationQuestionStep
